import { useCallback } from 'react';

import { Building, BuildingCategory } from './constants';
import { useGameTick } from './hooks/useGameTick';
import { InputCommand, InputCommandType } from './InputCommands';
import { InputManager } from './InputManager';
import { inputLogger } from './Logger';

// Game state interface for input handling
interface InputGameState {
  saveGame: () => boolean;
}

// Input hook parameters interface
interface UseKeyboardInputParams {
  gameState: InputGameState;
  buildMenuOpen?: boolean;
  setBuildMenuOpen?: (open: boolean) => void;
  setSelectedCategory?: (category: BuildingCategory | null) => void;
  setSelectedBuilding?: (building: Building | null) => void;
  setInventoryOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

// Custom hook for handling UI-specific input commands from InputManager
// Note: Game logic commands (MOVE, BUILD_DECONSTRUCT, etc.) are now handled by GameEngine during ticks
export const useKeyboardInput = ({
  gameState,
  buildMenuOpen = false,
  setBuildMenuOpen,
  setSelectedCategory,
  setSelectedBuilding,
  setInventoryOpen,
}: UseKeyboardInputParams): void => {
  // Process UI-only commands that need immediate React state updates
  const processUICommand = useCallback(
    (command: InputCommand) => {
      switch (command.type) {
        case InputCommandType.TOGGLE_BUILD_MENU:
          if (setBuildMenuOpen) {
            setBuildMenuOpen(!buildMenuOpen);
            if (setSelectedCategory) setSelectedCategory(null);
            if (setSelectedBuilding) setSelectedBuilding(null);
          }
          break;

        case InputCommandType.SELECT_BUILD_CATEGORY:
          if (setSelectedCategory) {
            const categoryCommand = command as { category: BuildingCategory };
            setSelectedCategory(categoryCommand.category);
          }
          break;

        case InputCommandType.SAVE_GAME:
          gameState.saveGame();
          break;

        case InputCommandType.TOGGLE_INVENTORY:
          if (setInventoryOpen) {
            setInventoryOpen(prev => !prev);
          }
          break;

        case InputCommandType.CLOSE_ALL_MENUS:
          if (setInventoryOpen) {
            setInventoryOpen(false);
          }
          break;
      }
    },
    [
      buildMenuOpen,
      setBuildMenuOpen,
      setSelectedBuilding,
      setSelectedCategory,
      gameState,
      setInventoryOpen,
    ]
  );

  // Process UI commands during game ticks for deterministic behavior
  const processUICommands = useCallback(() => {
    const inputManager = InputManager.getInstance();

    // Get UI commands from the UI queue (this consumes them)
    const commands = inputManager.getUICommands();

    if (commands.length > 0) {
      inputLogger.debug('processUICommands: Processing commands', {
        commandCount: commands.length,
        commandTypes: commands.map(c => c.type),
      });
    }

    // Process all UI commands
    for (const command of commands) {
      processUICommand(command);
    }
  }, [processUICommand]);

  // Subscribe to game ticks to process UI commands synchronously
  useGameTick(processUICommands, 1, true);
};
